import { apiClient } from './client';
import type { Asset, AssetArcResponse, AssetDefenderBadges, AssetDefenderResponse, AssetDetail, AssetHistoryResponse, AssetStatusHistory, AssetWrite, IntuneOrphan, PagedResult, StatusChange } from '@/types/oscar';

export interface AssetsQuery {
  category?: string;
  lifecycle?: string;
  make?: string;
  intune?: string;
  /** One location id, or several comma-separated — the API accepts both (multi-select). */
  location?: number | string;
  hasNote?: boolean;
  activity?: 'active' | 'inactive';
  primaryUserMismatch?: boolean;
  /** Enrolled devices assigned to a person whose Intune primary user is NOT that person (or is empty). */
  primaryUserVsAssignee?: boolean;
  /** Enrolled devices with no open assignment whose Intune primary user is an active Oscar person. */
  intuneUserUnassigned?: boolean;
  /** Azure Arc servers: any still in Arc, those with the Defender sensor deployed, or in Arc without it. */
  arc?: 'servers' | 'defender' | 'nodefender';
  /** Defender health Intune reports for Windows devices (Intune's view, not the Defender portal's). */
  defender?: 'healthy' | 'attention' | 'unknown';
  search?: string;
  warrantyExpiringDays?: number;
  // Per-column filters (filter row under the table headers).
  serial?: string;
  hostname?: string;
  assetTag?: string;
  makeModel?: string;
  warranty?: 'expiring' | 'expired';
  note?: 'has' | 'none';
  personId?: number;
  /** Assets this person is responsible for at a location — distinct from personId. */
  heldByPersonId?: number;
  /** Include soft-deleted assets (for recovery/restore). */
  includeDeleted?: boolean;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function qs(q: AssetsQuery): string {
  const p = new URLSearchParams();
  if (q.category) p.set('category', q.category);
  if (q.lifecycle) p.set('lifecycle', q.lifecycle);
  if (q.make) p.set('make', q.make);
  if (q.intune) p.set('intune', q.intune);
  if (q.location) p.set('location', String(q.location));
  if (q.hasNote) p.set('hasNote', '1');
  if (q.activity) p.set('activity', q.activity);
  if (q.primaryUserMismatch) p.set('primaryUserMismatch', '1');
  if (q.primaryUserVsAssignee) p.set('primaryUserVsAssignee', '1');
  if (q.intuneUserUnassigned) p.set('intuneUserUnassigned', '1');
  if (q.arc) p.set('arc', q.arc);
  if (q.defender) p.set('defender', q.defender);
  if (q.serial) p.set('serial', q.serial);
  if (q.hostname) p.set('hostname', q.hostname);
  if (q.assetTag) p.set('assetTag', q.assetTag);
  if (q.makeModel) p.set('makeModel', q.makeModel);
  if (q.warranty) p.set('warranty', q.warranty);
  if (q.note) p.set('note', q.note);
  if (q.search) p.set('search', q.search);
  if (q.warrantyExpiringDays) p.set('warrantyExpiringDays', String(q.warrantyExpiringDays));
  if (q.personId) p.set('personId', String(q.personId));
  if (q.heldByPersonId) p.set('heldByPersonId', String(q.heldByPersonId));
  if (q.includeDeleted) p.set('includeDeleted', '1');
  if (q.sort) p.set('sort', q.sort);
  if (q.dir) p.set('dir', q.dir);
  if (q.page) p.set('page', String(q.page));
  if (q.pageSize) p.set('pageSize', String(q.pageSize));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function listAssets(query: AssetsQuery = {}, signal?: AbortSignal): Promise<PagedResult<Asset>> {
  return apiClient.get<PagedResult<Asset>>(`/api/assets${qs(query)}`, signal);
}
export function getAsset(id: number, signal?: AbortSignal): Promise<AssetDetail> {
  return apiClient.get<AssetDetail>(`/api/assets/${id}`, signal);
}
export function createAsset(body: AssetWrite): Promise<Asset> {
  return apiClient.post<Asset>('/api/assets', body);
}
export function updateAsset(id: number, body: AssetWrite): Promise<Asset> {
  return apiClient.patch<Asset>(`/api/assets/${id}`, body);
}
/** POST /api/assets/:id/restore — un-delete a soft-deleted asset (admin). */
export function restoreAsset(id: number): Promise<Asset> {
  return apiClient.post<Asset>(`/api/assets/${id}/restore`);
}

export interface AssignBody {
  AssigneeKind: 'Person' | 'Location' | 'Fleet';
  PersonId?: number;
  LocationId?: number;
  FleetItemId?: string;
  /** Responsible person for an asset held at a location. Location assignments only. */
  HeldByPersonId?: number;
  Notes?: string;
  Status?: string;
  AssetLocationId?: number;
  /** Replaces the asset's notes outright. Empty string clears them; omit to leave alone. */
  NotesText?: string;
  /** Adds a line to the existing notes. Mutually exclusive with NotesText. */
  AppendNote?: string;
}
export function assignAsset(id: number, body: AssignBody): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/assign`, body);
}
export function returnAsset(id: number): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/return`);
}
/** Assign many assets in one step. Returns what actually changed. */
export interface BulkAssignBody {
  AssetIds: number[];
  AssigneeKind: 'Person' | 'Location' | 'Fleet';
  PersonId?: number;
  LocationId?: number;
  FleetItemId?: string;
  HeldByPersonId?: number;
  Status?: string;
  AssetLocationId?: number;
  Note?: string;
  /** 'Append' (default) adds to each asset's notes; 'Replace' overwrites them. */
  NoteMode?: 'Append' | 'Replace';
}
export interface BulkAssignResult {
  Requested: number;
  Updated: number;
  SkippedAssetIds: number[];
}
export function bulkAssignAssets(body: BulkAssignBody): Promise<BulkAssignResult> {
  return apiClient.post<BulkAssignResult>('/api/assets/bulk-assign', body);
}

/** Set or clear the responsible person on the open assignment. `null` clears it. */
export function setAssetHeldBy(id: number, heldByPersonId: number | null): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/held-by`, { HeldByPersonId: heldByPersonId });
}
/** Replace the asset's CMDB service links with the given set (CMDB Slice 2). */
/** Replace an asset's service links. Each carries an optional environment (ADR-002). */
export function setAssetServices(id: number, services: { ServiceId: number; EnvironmentId: number | null }[]): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/services`, { Services: services });
}
/** Manual lifecycle status change + status-history detail (POST /status). */
export function setAssetStatus(id: number, body: StatusChange): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/status`, body);
}
/**
 * GET /api/assets/:id/history - where the asset has been and whose it was, as one timeline: assignments, returns,
 * its own location, the Intune primary user and status changes, newest first.
 */
export function getAssetHistory(id: number, signal?: AbortSignal): Promise<AssetHistoryResponse> {
  return apiClient.get<AssetHistoryResponse>(`/api/assets/${id}/history`, signal);
}

/** What Azure Arc says about an asset, or `{ Arc: null }` when it is not an Arc server. */
export function getAssetArc(id: number, signal?: AbortSignal): Promise<AssetArcResponse> {
  return apiClient.get<AssetArcResponse>(`/api/assets/${id}/arc`, signal);
}

/** Defender / Arc labels for the asset ids on a register page (one call for the page; fail-soft on the server). */
export function getAssetDefenderBadges(ids: number[], signal?: AbortSignal): Promise<AssetDefenderBadges> {
  return apiClient.get<AssetDefenderBadges>(`/api/assets/defender-badges?ids=${ids.join(',')}`, signal);
}

export function getAssetDefender(id: number, signal?: AbortSignal): Promise<AssetDefenderResponse> {
  return apiClient.get<AssetDefenderResponse>(`/api/assets/${id}/defender`, signal);
}

export function getAssetStatusHistory(id: number, signal?: AbortSignal): Promise<AssetStatusHistory[]> {
  return apiClient.get<AssetStatusHistory[]>(`/api/assets/${id}/status-history`, signal);
}
export function deleteAsset(id: number): Promise<{ AssetId: number; IsDeleted: boolean }> {
  return apiClient.post(`/api/assets/${id}/delete`);
}
export function refreshWarranty(id: number): Promise<unknown> {
  return apiClient.post(`/api/assets/${id}/refresh-warranty`);
}

// SIM-in-handset links (PR-19).
export interface LinkBody {
  ChildAssetId: number;
  LinkType?: 'SimInHandset';
  Notes?: string;
}
export function linkAsset(id: number, body: LinkBody): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/links`, body);
}
export function unlinkAsset(id: number, linkId: number): Promise<AssetDetail> {
  return apiClient.post<AssetDetail>(`/api/assets/${id}/links/${linkId}/unlink`);
}

export function listIntuneOrphans(signal?: AbortSignal): Promise<IntuneOrphan[]> {
  return apiClient.get<IntuneOrphan[]>('/api/assets/intune-orphans', signal);
}
export function patchIntuneOrphan(id: number, body: { TriageStatus: string; Notes?: string }): Promise<unknown> {
  return apiClient.patch(`/api/assets/intune-orphans/${id}`, body);
}
